import React, { useCallback } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';

import {
  HostedFrame,
  type HostedFrameMessageEvent,
  ModalDialogue,
  theme,
  useNoticeBannersStackActions,
} from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useSendLogoutToAuthMachine } from '../../../../../../../context/AuthMachine';
import { getAzureAuthEmbeddedFrameLogoutUrl } from '../../../../../../../context/AzureAuth/helpers/get-azure-auth-embedded-frame-url';
import { removeTokens } from '../../../../../../../context/AzureAuth/helpers/remove-tokens';
import { useCustomer } from '../../../../../../../context/Customer';
import { LoadingAnimation } from '../../../../../../LoadingAnimation';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountMenuSignOutItemEmbeddedModal = (props: AccountMenuSignOutItemEmbeddedProps) => {
  const { push: addBanner } = useNoticeBannersStackActions();
  const { track } = useTelemetry();
  const { signOut } = useCustomer();
  const sendLogoutAction = useSendLogoutToAuthMachine();

  const { isVisible, closeModal, resetAppState } = props;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const closeModalAndSignOut = useCallback(async () => {
    const hasSignedOut = await signOut();

    if (!hasSignedOut) return;

    sendLogoutAction();
    await removeTokens();
    resetAppState();
    closeModal();
  }, [closeModal, resetAppState, sendLogoutAction, signOut]);

  const onMessage = useCallback(
    (event: HostedFrameMessageEvent) => {
      const data = JSON.parse(event?.nativeEvent?.data ?? '{}');
      const eventType = data?.type;

      if (!eventType) return;

      switch (eventType) {
        case 'LOGOUT_SUCCESS': {
          void closeModalAndSignOut();
          track('azure-ad.logout.success');

          break;
        }

        case 'LOGOUT_ERROR': {
          closeModal();
          addBanner({
            palette: 'caution',
            text: 'Logging out failed. Please try again.',
          });
          track('azure-ad.logout.failure');

          break;
        }
      }
    },
    [closeModalAndSignOut, track, closeModal, addBanner],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue size="small" visible={isVisible}>
      <SafeAreaView style={styles.container} edges={SAFE_AREA_EDGES}>
        <LoadingAnimation size="small" />
      </SafeAreaView>

      {isVisible ? (
        <HostedFrame
          frameId="azure-auth-sign-out-frame"
          frameHeight={0}
          source={AUTH_FRAME_SRC}
          onMessage={onMessage}
        />
      ) : null}
    </ModalDialogue>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['bottom'],
  default: [],
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: theme.spacing['6'],
    paddingBottom: theme.spacing['6'],
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AUTH_FRAME_SRC = {
  uri: getAzureAuthEmbeddedFrameLogoutUrl(),
};

// ─── Types ───────────────────────────────────────────────────────────────────

type AccountMenuSignOutItemEmbeddedProps = {
  isVisible: boolean;
  closeModal: () => void;
  resetAppState: () => void;
};
